import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { FiArrowRight, FiStar, FiZap, FiPlay } from 'react-icons/fi';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const Hero = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const buttonsRef = useRef(null);
  const statsRef = useRef(null);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    
    // GSAP Animations
    // Stagger animation for title words
    if (titleRef.current) {
      const words = titleRef.current.querySelectorAll('.word');
      gsap.fromTo(words, 
        { 
          y: 100, 
          opacity: 0,
          rotationX: 90
        },
        { 
          y: 0, 
          opacity: 1,
          rotationX: 0,
          duration: 1,
          stagger: 0.3,
          ease: "back.out(1.7)",
          delay: 0.5
        }
      );
    }
    
    // Subtitle animation
    if (subtitleRef.current) {
      gsap.fromTo(subtitleRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, delay: 1.5 }
      );
    }
    
    // Buttons animation
    if (buttonsRef.current) {
      const buttons = buttonsRef.current.children;
      gsap.fromTo(buttons,
        { y: 30, opacity: 0, scale: 0.8 },
        { 
          y: 0, 
          opacity: 1, 
          scale: 1,
          duration: 0.6,
          stagger: 0.2,
          ease: "back.out(1.7)",
          delay: 2
        }
      );
    }
    
    // Stats animation
    if (statsRef.current) {
      const stats = statsRef.current.children;
      gsap.fromTo(stats,
        { y: 20, opacity: 0 },
        { 
          y: 0, 
          opacity: 1,
          duration: 0.8,
          stagger: 0.1,
          delay: 2.5
        }
      );
    }

    // Floating elements with GSAP
    const floatingElements = heroRef.current?.querySelectorAll('.floating-element');
    if (floatingElements) {
      floatingElements.forEach((element, index) => {
        gsap.to(element, {
          y: index % 2 === 0 ? -20 : 20,
          rotation: index % 2 === 0 ? 5 : -5,
          duration: 3 + index,
          repeat: -1,
          yoyo: true,
          ease: "power2.inOut",
          delay: index * 0.5
        });
      });
    }

    // Create animated particles
    const createParticles = () => {
      for (let i = 0; i < 30; i++) {
        const particle = document.createElement('div');
        particle.className = 'absolute w-1 h-1 bg-white/20 rounded-full';
        particle.style.left = `${Math.random() * 100}%`;
        particle.style.top = `${Math.random() * 100}%`;
        heroRef.current?.appendChild(particle);
        
        gsap.to(particle, {
          y: -200,
          opacity: 0,
          duration: 3 + Math.random() * 3,
          repeat: -1,
          delay: Math.random() * 3,
          ease: "power1.out"
        });
        
        gsap.to(particle, {
          x: (Math.random() - 0.5) * 100,
          duration: 4 + Math.random() * 2,
          repeat: -1,
          yoyo: true,
          ease: "power1.inOut"
        });
      }
    };
    
    createParticles();

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <section 
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24 md:pt-20"
    >
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-dark-900 via-dark-800 to-secondary-900"></div>
        
        {/* Floating Elements */}
        <motion.div
          animate={{
            x: mousePosition.x * 0.01,
            y: mousePosition.y * 0.01,
          }}
          className="absolute top-20 left-20 w-72 h-72 bg-gradient-to-r from-secondary-500/20 to-accent-500/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{
            x: mousePosition.x * -0.01,
            y: mousePosition.y * -0.01,
          }}
          className="absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-r from-accent-500/20 to-primary-500/20 rounded-full blur-3xl"
        />
      </div>

      <div className="relative z-10 container mx-auto px-4 md:px-6 text-center">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          whileHover={{ scale: 1.05 }}
          className="inline-flex items-center space-x-2 bg-gradient-to-r from-secondary-500/20 to-accent-500/20 border border-secondary-500/30 rounded-full px-6 py-2 mb-8"
        >
          <FiStar className="w-4 h-4 text-secondary-400" />
          <span className="text-sm text-gray-300">AI-Powered Conversations</span>
        </motion.div>

        {/* Main Heading */}
        <div ref={titleRef} className="mb-6 px-4">
          <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-display font-bold leading-tight">
            <span className="word block">Talk to</span>
            <span className="word block gradient-text animate-gradient-x bg-gradient-to-r from-secondary-400 via-accent-400 to-primary-400">
              Multiple AIs
            </span>
            <span className="word block">in One Place</span>
          </h1>
        </div>

        {/* Subtitle */}
        <p
          ref={subtitleRef}
          className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed px-4"
        >
          Experience the future of AI interaction with Bervin. Chat with multiple AI models simultaneously, 
          get diverse perspectives, and unlock unprecedented productivity.
        </p>

        {/* CTA Buttons */}
        <div
          ref={buttonsRef}
          className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-12"
        >
          <motion.button
            whileHover={{ 
              scale: 1.05, 
              boxShadow: "0 20px 40px rgba(168, 85, 247, 0.3)" 
            }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.preventDefault();
              try {
                window.open('https://bervin.appiworks.com/app', '_blank', 'noopener,noreferrer');
              } catch (error) {
                window.location.href = 'https://bervin.appiworks.com/app';
              }
            }}
            className="group relative px-8 py-4 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-xl font-semibold text-white text-lg shadow-2xl hover-lift overflow-hidden cursor-pointer"
          >
            <span className="relative z-10 flex items-center">
              Try Bervin Now
              <motion.div
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="ml-2"
              >
                <FiArrowRight className="w-5 h-5" />
              </motion.div>
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-secondary-600 to-accent-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </motion.button>

          <motion.button
            whileHover={{ 
              scale: 1.05, 
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)" 
            }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.preventDefault();
              window.open('https://www.youtube.com/watch?v=Hjh0zeRmOtw', '_blank', 'noopener,noreferrer');
            }}
            className="group relative px-8 py-4 bg-transparent border-2 border-white/20 rounded-xl font-semibold text-white text-lg shadow-xl hover-lift overflow-hidden cursor-pointer backdrop-blur-sm"
          >
            <span className="relative z-10 flex items-center">
              <FiPlay className="w-5 h-5 mr-2" />
              Watch Demo
            </span>
            <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </motion.button>
        </div>

        {/* Stats */}
        <div
          ref={statsRef}
          className="flex flex-col sm:flex-row items-center justify-center space-y-6 sm:space-y-0 sm:space-x-12"
        >
          {[
            { number: "10+", label: "AI Models" },
            { number: "50K+", label: "Active Users" },
            { number: "1M+", label: "Conversations" },
          ].map((stat, index) => (
            <div
              key={stat.label}
              className="text-center"
            >
              <div className="text-3xl md:text-4xl font-bold gradient-text mb-1">
                {stat.number}
              </div>
              <div className="text-gray-400">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Floating Elements */}
        <div
          className="floating-element absolute top-32 left-10 w-16 h-16 bg-gradient-to-r from-secondary-500/30 to-accent-500/30 rounded-xl blur-sm"
        />
        <div
          className="floating-element absolute bottom-32 right-10 w-20 h-20 bg-gradient-to-r from-accent-500/30 to-primary-500/30 rounded-full blur-sm"
        />
        
        {/* Lightning Bolt */}
        <motion.div
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.5, 1, 0.5]
          }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-40 right-20 text-accent-400"
        >
          <FiZap size={24} />
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 3, duration: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-1 h-3 bg-gradient-to-b from-secondary-500 to-accent-500 rounded-full mt-2"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;
